import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, Envelope, DeviceMobile, PaperPlaneTilt, Info } from "@phosphor-icons/react";

const ALERT_LABELS = {
  below_par: "Below PAR",
  out_of_stock: "Out of Stock",
  count_awaiting_approval: "Count awaiting approval",
  sync_failure: "Offline sync failure",
  expiring_soon: "Expiring soon (reserved)",
};

const ROLES = ["system_owner", "admin", "supervisor", "inventory_user", "viewer"];
const CADENCES = ["immediate", "hourly", "daily", "disabled"];
const CHANNELS = ["email", "push", "both", "none"];

export default function NotificationSettings() {
  const [status, setStatus] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [seed, setSeed] = useState(null);
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sRes, rRes] = await Promise.all([
        api.get("/notifications/status"),
        api.get("/notifications/role-defaults"),
      ]);
      setStatus(sRes.data);
      setDefaults(rRes.data.role_defaults);
      setSeed(rRes.data.seed_defaults);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRole = async (role) => {
    setSaving({ ...saving, [role]: true });
    try {
      await api.put("/notifications/role-defaults", { role, prefs: defaults[role] });
      toast.success(`${role} defaults saved`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving({ ...saving, [role]: false });
    }
  };

  const resetRole = (role) => {
    setDefaults({ ...defaults, [role]: JSON.parse(JSON.stringify(seed[role])) });
    toast.info(`${role} reverted to seed defaults — click Save to persist.`);
  };

  const updateCell = (role, alertType, field, value) => {
    setDefaults({
      ...defaults,
      [role]: {
        ...defaults[role],
        [alertType]: { ...defaults[role][alertType], [field]: value },
      },
    });
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/notifications/test-send", { alert_type: "below_par" });
      toast.success(`Test alert dispatched (id=${data.alert_id || "n/a"})`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  if (!status || !defaults) return <div className="p-6 text-sm text-muted-foreground font-mono">Loading…</div>;

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <Link to="/admin" className="text-xs text-muted-foreground hover:text-primary">← Admin</Link>
      <div className="my-3">
        <div className="label-tech mb-1">Notifications</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Notification Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure role-level default channel + cadence for each alert type. Users can override in their own preferences.</p>
      </div>

      {/* Status pills */}
      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <div className="card-flat border-border p-4" data-testid="admin-email-mode">
          <div className="flex items-center gap-2 mb-1">
            <Envelope size={16} weight="bold" className="text-primary" />
            <div className="label-tech">Email mode</div>
          </div>
          <div className="font-display font-black text-lg">
            {status.email_mode === "live" ? "Live (Resend)" : "Dry Run"}
          </div>
          <div className="text-xs text-muted-foreground font-mono mt-1">
            {status.email_mode === "live" ? `Sender: ${status.email_sender}` : "No RESEND_API_KEY set — emails are logged only."}
          </div>
        </div>
        <div className="card-flat border-border p-4" data-testid="admin-push-mode">
          <div className="flex items-center gap-2 mb-1">
            <DeviceMobile size={16} weight="bold" className="text-primary" />
            <div className="label-tech">Web Push</div>
          </div>
          <div className="font-display font-black text-lg">{status.web_push_enabled ? "Enabled" : "Disabled"}</div>
          <div className="text-xs text-muted-foreground font-mono mt-1">
            {status.web_push_enabled ? "VAPID keypair loaded." : "Missing VAPID_PRIVATE_KEY_PATH."}
          </div>
        </div>
        <div className="card-flat border-border p-4">
          <div className="flex items-center gap-2 mb-1">
            <PaperPlaneTilt size={16} weight="bold" className="text-primary" />
            <div className="label-tech">Test send</div>
          </div>
          <div className="text-sm text-muted-foreground mb-2">
            Dispatch a synthetic <span className="font-mono">below_par</span> alert to yourself.
          </div>
          <button
            data-testid="admin-test-send-btn"
            onClick={runTest}
            disabled={testing}
            className="btn-hard h-9 px-4 text-[11px] font-bold uppercase tracking-widest rounded-sm bg-primary text-primary-foreground hover:bg-orange-600 disabled:opacity-50"
          >
            {testing ? "Sending…" : "Send Test Alert"}
          </button>
        </div>
      </div>

      {/* Setup hint when in dry-run */}
      {status.email_mode === "dry_run" && (
        <div className="card-flat border-primary/40 bg-primary/5 p-4 mb-6 text-sm flex gap-3">
          <Info size={20} weight="bold" className="text-primary shrink-0 mt-0.5" />
          <div>
            <div className="font-bold mb-1">Enable real email delivery</div>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li>Create an API key at <span className="font-mono">resend.com/api-keys</span>.</li>
              <li>Set <span className="font-mono">RESEND_API_KEY=&quot;re_...&quot;</span> in <span className="font-mono">/app/backend/.env</span>.</li>
              <li>Optionally set <span className="font-mono">RESEND_SENDER</span> to your verified domain sender (default: <span className="font-mono">onboarding@resend.dev</span>, test-mode only).</li>
              <li>Restart backend: <span className="font-mono">sudo supervisorctl restart backend</span>.</li>
            </ol>
          </div>
        </div>
      )}

      {/* Role-defaults matrix (one card per role) */}
      <div className="space-y-4">
        {ROLES.map((role) => (
          <div key={role} className="card-flat border-border p-4" data-testid={`role-${role}`}>
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="label-tech mb-1">Role</div>
                <div className="font-display font-black text-xl uppercase tracking-widest">{role.replace("_", " ")}</div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => resetRole(role)}
                  className="btn-hard h-9 px-3 border border-border text-[11px] font-bold uppercase tracking-widest rounded-sm hover:bg-muted"
                >
                  Revert to seed
                </button>
                <button
                  data-testid={`save-role-${role}`}
                  onClick={() => saveRole(role)}
                  disabled={!!saving[role]}
                  className="btn-hard h-9 px-4 bg-primary text-primary-foreground text-[11px] font-bold uppercase tracking-widest rounded-sm hover:bg-orange-600 disabled:opacity-50 flex items-center gap-1"
                >
                  <FloppyDisk size={12} weight="bold" /> {saving[role] ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-[1.5fr_1fr_1fr] gap-2 px-2 py-2 label-tech border-b border-border">
              <div>Alert</div><div>Channel</div><div>Cadence</div>
            </div>
            {Object.entries(ALERT_LABELS).map(([k, label]) => {
              const p = defaults[role][k] || { channel: "none", cadence: "disabled" };
              const disabled = k === "expiring_soon";
              return (
                <div key={k} className="grid grid-cols-[1.5fr_1fr_1fr] gap-2 items-center px-2 py-2 border-b border-border last:border-b-0">
                  <div className="text-sm">{label}</div>
                  <select
                    value={p.channel}
                    disabled={disabled}
                    onChange={(e) => updateCell(role, k, "channel", e.target.value)}
                    className="h-9 px-2 border border-border bg-background rounded-sm text-sm disabled:opacity-40"
                  >
                    {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select
                    value={p.cadence}
                    disabled={disabled}
                    onChange={(e) => updateCell(role, k, "cadence", e.target.value)}
                    className="h-9 px-2 border border-border bg-background rounded-sm text-sm disabled:opacity-40"
                  >
                    {CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
